/**
 * useDashboardTabs Hook
 *
 * Manages dashboard tab navigation and configuration.
 * Handles tab state, tab change, and statistics card click navigation.
 * Includes loading state management for tab switching animations.
 */

import { useState, useCallback, useMemo, useRef, type RefObject } from 'react';
import { useSearchParams } from 'react-router';
import { parseURLFilters } from '../../types/search';
import { useMinimumLoadingDuration } from '../core/useMinimumLoadingDuration';

/**
 * Tab configuration for Polaris Tabs component
 */
export interface TabConfig {
  id: string;
  content: string;
  badge?: string;
  panelID: string;
  accessibilityLabel?: string;
}

/**
 * Tab badge counts
 */
export interface TabCounts {
  activeAlertsCount: number;
  monitoredProductsCount: number;
}

/**
 * Return type for useDashboardTabs hook
 */
export interface UseDashboardTabsReturn {
  // State
  selectedTab: number;
  tabs: TabConfig[];
  /** True during brief loading state when switching tabs */
  isTabSwitching: boolean;
  /** The previous tab index (useful for transition animations) */
  previousTab: number;

  // Handlers
  handleTabChange: (selectedTabIndex: number) => void;
  handleStatisticsCardClick: (tabIndex: number, stockAlertsRef?: RefObject<HTMLDivElement | null>) => void;
}

/**
 * Hook for managing dashboard tab navigation
 *
 * @param counts - Badge counts for tabs
 * @returns Tab state and handlers
 */
/**
 * Duration (ms) for tab switch loading state.
 * Set to 0 for instant switching - there's no actual async data loading during tab switch.
 * The tab content is already loaded; this was previously 150ms which added unnecessary delay.
 * Keep the infrastructure in place for future async operations if needed.
 */
const TAB_SWITCH_DURATION = 0;

export function useDashboardTabs(counts: TabCounts): UseDashboardTabsReturn {
  const [searchParams] = useSearchParams();

  // Parse initial tab from URL
  const initialFilters = parseURLFilters(searchParams);
  const [selectedTab, setSelectedTab] = useState(() => {
    const tabParam = initialFilters.tab;
    return tabParam ? parseInt(tabParam, 10) : 0;
  });

  // Track previous tab for transitions
  const previousTabRef = useRef(selectedTab);
  const [previousTab, setPreviousTab] = useState(selectedTab);

  // Tab switching loading state
  const [rawIsTabSwitching, setRawIsTabSwitching] = useState(false);
  const isTabSwitching = useMinimumLoadingDuration(rawIsTabSwitching, {
    minimumDuration: TAB_SWITCH_DURATION,
  });

  // Timeout ref for cleanup
  const switchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll position tracking per tab (for preserving scroll when switching tabs)
  const scrollPositions = useRef<Map<number, number>>(new Map());

  // Build tabs configuration with dynamic badges
  const tabs = useMemo((): TabConfig[] => {
    const { activeAlertsCount, monitoredProductsCount } = counts;

    return [
      {
        id: 'active-alerts',
        content: 'Active Alerts',
        badge: activeAlertsCount > 0 ? String(activeAlertsCount) : undefined,
        panelID: 'active-alerts-panel',
        accessibilityLabel: `Active Alerts (${activeAlertsCount})`,
      },
      {
        id: 'monitored-products',
        content: 'Monitored Products',
        badge: monitoredProductsCount > 0 ? String(monitoredProductsCount) : undefined,
        panelID: 'monitored-products-panel',
        accessibilityLabel: `Monitored Products (${monitoredProductsCount})`,
      },
      {
        id: 'alert-history',
        content: 'Alert History',
        panelID: 'alert-history-panel',
      },
    ];
  }, [counts]);

  /**
   * Handle tab change with loading state
   */
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    // Skip if same tab
    if (selectedTabIndex === selectedTab) return;

    // Clear any pending timeout
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
    }

    // 1. Capture current scroll position BEFORE switching
    // Use window.scrollY for cross-browser compatibility in iframe contexts
    const currentScrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    scrollPositions.current.set(selectedTab, currentScrollTop);

    // Store previous tab before changing
    setPreviousTab(previousTabRef.current);

    // Start loading state
    setRawIsTabSwitching(true);

    // Update selected tab
    setSelectedTab(selectedTabIndex);
    previousTabRef.current = selectedTabIndex;

    // 2. Restore scroll position AFTER render settles
    // Use double requestAnimationFrame to ensure DOM has fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const savedPosition = scrollPositions.current.get(selectedTabIndex) ?? 0;
        const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const clampedPosition = Math.min(savedPosition, maxScrollTop);

        window.scrollTo({
          top: clampedPosition,
          behavior: 'auto', // Instant - no jarring animation
        });
      });
    });

    // End loading state after tab switch duration
    switchTimeoutRef.current = setTimeout(() => {
      setRawIsTabSwitching(false);
    }, TAB_SWITCH_DURATION);
  }, [selectedTab]);

  /**
   * Handle statistics card click - switch to tab and scroll to Stock Alerts section
   */
  const handleStatisticsCardClick = useCallback((tabIndex: number, stockAlertsRef?: RefObject<HTMLDivElement | null>) => {
    // Clear saved scroll position - we're intentionally scrolling to a specific element
    scrollPositions.current.delete(tabIndex);

    // Only switch tab if not already selected
    if (selectedTab !== tabIndex) {
      setSelectedTab(tabIndex);
    }

    // Scroll to bottom of page to show all products in the table
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  }, [selectedTab]);

  return {
    // State
    selectedTab,
    tabs,
    isTabSwitching,
    previousTab,

    // Handlers
    handleTabChange,
    handleStatisticsCardClick,
  };
}
